import { CustomerBadgeId } from "@/dashboard/features/hub/lib/customer/ui/badge";
import {
  BusinessUnitSelection,
  formatBusinessUnitSelection,
} from "@/dashboard/features/hub/lib/filters/business-unit-selection";
import { FolderSelection } from "@/dashboard/features/hub/lib/folders/constants";
import { HubTab } from "@/dashboard/types/hub";
import {
  HubInteractionChannelTypeFilter,
  HubInteractionTypeFilter,
} from "@/dashboard/types/interaction";

export enum HubAssignmentFilter {
  MINE = "mine",
  UNASSIGNED = "unassigned",
}

export interface HubFilters {
  /** ISO (YYYY‑MM‑DD) UTC midnight */
  start?: Date;
  /** ISO (YYYY‑MM‑DD) UTC inclusive (23:59:59.999) */
  end?: Date;
  assignment?: HubAssignmentFilter;
  assignedToUserIds?: string[];
  assignedToUserGroupIds?: string[];
  modalities?: HubInteractionTypeFilter[];
  channelTypes?: HubInteractionChannelTypeFilter[];
  badges?: CustomerBadgeId[];
  campaigns?: string[];
  businessUnits?: BusinessUnitSelection[];
  audienceMemberStatus?: string[];
  folder?: FolderSelection;
  hasUnrespondedText?: boolean;
  hasResponse?: boolean;
  digitalLeadSources?: string[];
  homeClubs?: string[];
  contactTypes?: string[];
  /** Transfer department keys; "DEFAULT" is the default line. Applies to the Transferred tab only. */
  transferDepartments?: string[];
}

export const filtersKey = (f: HubFilters) =>
  JSON.stringify([
    f.start?.toISOString(),
    f.end?.toISOString(),
    f.assignment,
    ...(f.modalities?.slice().sort() ?? []),
    ...(f.channelTypes?.slice().sort() ?? []),
    ...(f.badges?.slice().sort() ?? []),
    ...(f.campaigns?.slice().sort() ?? []),
    ...(f.businessUnits?.map(formatBusinessUnitSelection).sort() ?? []),
    ...(f.audienceMemberStatus?.slice().sort() ?? []),
    ...(f.assignedToUserIds?.slice().sort() ?? []),
    ...(f.assignedToUserGroupIds?.slice().sort() ?? []),
    f.folder?.folderName,
    ...(f.folder?.tenantIds?.slice().sort() ?? []),
    f.hasUnrespondedText,
    f.hasResponse,
    ...(f.digitalLeadSources?.slice().sort() ?? []),
    ...(f.homeClubs?.slice().sort() ?? []),
    ...(f.contactTypes?.slice().sort() ?? []),
    ...(f.transferDepartments?.slice().sort() ?? []),
  ]);

export const isNonDateFilterApplied = (f: HubFilters) =>
  !!(
    f.assignment ||
    f.assignedToUserIds?.length ||
    f.assignedToUserGroupIds?.length ||
    f.modalities?.length ||
    f.channelTypes?.length ||
    f.badges?.length ||
    f.campaigns?.length ||
    f.businessUnits?.length ||
    f.audienceMemberStatus?.length ||
    f.hasUnrespondedText ||
    f.hasResponse ||
    f.digitalLeadSources?.length ||
    f.homeClubs?.length ||
    f.contactTypes?.length ||
    f.transferDepartments?.length
  );

/** Whether `tab` lists transferred customers, the only tabs the transfer department filter applies to. */
export const isTransferredHubTab = (tab: HubTab) =>
  tab === HubTab.TRANSFERRED || tab === HubTab.TRANSFERRED_INTERNAL;

export const isFilterApplied = (f: HubFilters) => !!(f.start || f.end || isNonDateFilterApplied(f));
